import { DateTime } from 'luxon';
import * as z from 'zod';
import { requiredOr } from './utils/required-error.util.mjs';

const fileTypeDef = [
    "asset",
    "bank_line_summary",
    "banking_reference",
    "bid_package",
    "bid_results",
    "blank_bond_form",
    "blank_indemnity_agreement",
    "bond_form_template",
    "bond_request_form",
    "collateral_document",
    "company_credit_report",
    "company_ownership_chart",
    "company_tax_form",
    "compliance_certificate",
    "contract",
    "contract_summary",
    "contractors_questionnaire_application",
    "corporate_cash_verification",
    "credit_agreement",
    "filled_bond_form",
    "fye_company_ap_aging",
    "fye_company_ar_aging",
    "fye_company_balance_sheet",
    "fye_company_cash_flow_statement",
    "fye_company_income_statement",
    "indemnity_agreement",
    "indemnity_agreement_rider",
    "individual_cash_verification",
    "individual_credit_report",
    "individual_financial_statement",
    "individual_tax_form",
    "inrev_report",
    "interim_company_ap_aging",
    "interim_company_ar_aging",
    "interim_company_balance_sheet",
    "interim_company_cash_flow_statement",
    "interim_company_income_statement",
    "invoice",
    "job_cost_breakdown",
    "other_bank_statement",
    "other_bond_document",
    "other_company_financial_statement",
    "other_company_information",
    "other_indemnity_document",
    "other_individual_financial_statement",
    "other_wip",
    "poa",
    "real_estate_schedule",
    "rider",
    "shipping_label",
    "subordination_agreement",
    "trust_document",
    "wip",
];
const fileTypeSchema = z.enum(fileTypeDef);
const FileType = fileTypeSchema.enum;
const classifierSchema = z.enum(["ml", "decisions"]);
const classifyDeferReasonSchema = z.enum([
    "scanned",
    "other",
    "unsupported",
    "ocr_error",
    "ml_error",
    "unavailable",
]);
const decisionAnswerSchema = z.object({
    type: z.string(),
    name: z.string().nullish(),
    probability: z.number().nullish(),
    choice: z.string().nullish(),
});
const classificationPredictionSchema = z.object({
    modelName: z.string().optional(),
    modelVersion: z.string().optional(),
    route: z.string().optional(),
    tags: z.array(z.object({
        fileType: fileTypeSchema,
        probability: z.number().optional(),
    })),
    classifier: classifierSchema.optional(),
    mlSkipReason: classifyDeferReasonSchema.optional(),
    attempts: z.number().int().optional(),
    latencyMs: z.number().optional(),
    usage: z
        .object({ inputTokens: z.number().optional(), outputTokens: z.number().optional() })
        .optional(),
    answers: z.array(decisionAnswerSchema).optional(),
});
const companyBackgroundFileTypeDef = [
    FileType.contractors_questionnaire_application,
    FileType.company_ownership_chart,
    FileType.company_credit_report,
    FileType.other_company_information,
];
const corporateFinancialsFileTypeDef = [
    FileType.fye_company_balance_sheet,
    FileType.fye_company_income_statement,
    FileType.fye_company_cash_flow_statement,
    FileType.fye_company_ap_aging,
    FileType.fye_company_ar_aging,
    FileType.interim_company_balance_sheet,
    FileType.interim_company_income_statement,
    FileType.interim_company_cash_flow_statement,
    FileType.interim_company_ar_aging,
    FileType.interim_company_ap_aging,
    FileType.company_tax_form,
    FileType.other_company_financial_statement,
];
const wipFileTypeDef = [FileType.wip, FileType.other_wip];
const bankInformationFileTypeDef = [
    FileType.corporate_cash_verification,
    FileType.compliance_certificate,
    FileType.bank_line_summary,
    FileType.banking_reference,
    FileType.credit_agreement,
    FileType.other_bank_statement,
];
const personalFinancialsFileTypeDef = [
    FileType.individual_financial_statement,
    FileType.individual_cash_verification,
    FileType.individual_credit_report,
    FileType.individual_tax_form,
    FileType.trust_document,
    FileType.real_estate_schedule,
    FileType.other_individual_financial_statement,
];
const indemnityFileTypeDef = [
    FileType.indemnity_agreement,
    FileType.indemnity_agreement_rider,
    FileType.collateral_document,
    FileType.subordination_agreement,
    FileType.other_indemnity_document,
];
const bondFormFileTypeDef = [
    FileType.blank_bond_form,
    FileType.bond_request_form,
    FileType.bid_package,
    FileType.bid_results,
    FileType.contract,
    FileType.filled_bond_form,
    FileType.job_cost_breakdown,
    FileType.poa,
    FileType.shipping_label,
    FileType.invoice,
    FileType.rider,
    FileType.other_bond_document,
];
const financialStatementFileTypeDef = [
    FileType.fye_company_balance_sheet,
    FileType.fye_company_income_statement,
];
const generatedFileTypeDef = [FileType.inrev_report];
const generatedFileTypeSchema = z.enum(generatedFileTypeDef);
const GeneratedFileType = generatedFileTypeSchema.enum;
const attachmentFileTypeDef = [
    ...companyBackgroundFileTypeDef,
    ...corporateFinancialsFileTypeDef,
    ...wipFileTypeDef,
    ...bankInformationFileTypeDef,
    ...personalFinancialsFileTypeDef,
    ...indemnityFileTypeDef,
    ...bondFormFileTypeDef,
    ...generatedFileTypeDef,
];
const attachmentFileTypeSchema = z.enum(attachmentFileTypeDef);
const AttachmentFileType = attachmentFileTypeSchema.enum;
const financialStatementAttachmentFileTypeSchema = z.enum(financialStatementFileTypeDef);
const FinancialStatementAttachmentFileType = financialStatementAttachmentFileTypeSchema.enum;
const companyBackgroundAttachmentFileTypeSchema = z.enum(companyBackgroundFileTypeDef);
const CompanyBackgroundAttachmentFileType = companyBackgroundAttachmentFileTypeSchema.enum;
const corporateFinancialsAttachmentFileTypeSchema = z.enum(corporateFinancialsFileTypeDef);
const CorporateFinancialsAttachmentFileType = corporateFinancialsAttachmentFileTypeSchema.enum;
const wipAttachmentFileTypeSchema = z.enum(wipFileTypeDef);
const WipAttachmentFileType = wipAttachmentFileTypeSchema.enum;
const bankInformationAttachmentFileTypeSchema = z.enum(bankInformationFileTypeDef);
const BankInformationAttachmentFileType = bankInformationAttachmentFileTypeSchema.enum;
const personalFinancialsAttachmentFileTypeSchema = z.enum(personalFinancialsFileTypeDef);
const PersonalFinancialsAttachmentFileType = personalFinancialsAttachmentFileTypeSchema.enum;
const indemnityAttachmentFileTypeSchema = z.enum(indemnityFileTypeDef);
const IndemnityAttachmentFileType = indemnityAttachmentFileTypeSchema.enum;
const bondFormFileTypeSchema = z.enum(bondFormFileTypeDef);
const BondFormFileType = bondFormFileTypeSchema.enum;
const suretyAccountAttachmentFileTypeDef = [
    ...companyBackgroundFileTypeDef,
    ...corporateFinancialsFileTypeDef,
    ...wipFileTypeDef,
    ...bankInformationFileTypeDef,
    ...personalFinancialsFileTypeDef,
    ...indemnityFileTypeDef,
];
const suretyAccountAttachmentFileTypeSchema = z.enum(suretyAccountAttachmentFileTypeDef);
const SuretyAccountAttachmentFileType = suretyAccountAttachmentFileTypeSchema.enum;
const indemnificationTypeDef = ["contract_surety"];
const indemnificationTypeSchema = z.enum(indemnificationTypeDef);
const IndemnificationType = indemnificationTypeSchema.enum;
const indemnitorTypeDef = ["individual", "company"];
const indemnitorTypeSchema = z.enum(indemnitorTypeDef);
const IndemnitorType = indemnitorTypeSchema.enum;
var IndemnityAgreementSignatureMethod;
(function (IndemnityAgreementSignatureMethod) {
    IndemnityAgreementSignatureMethod["electronic"] = "electronic";
    IndemnityAgreementSignatureMethod["physical"] = "physical";
})(IndemnityAgreementSignatureMethod || (IndemnityAgreementSignatureMethod = {}));
const indemnityAgreementSignatureMethodDef = [
    IndemnityAgreementSignatureMethod.electronic,
    IndemnityAgreementSignatureMethod.physical,
];
const indemnityAgreementSignatureMethodSchema = z.enum(indemnityAgreementSignatureMethodDef);
(function (IndemnityAgreementSignatureMethod) {
    const labelMapInternal = {
        [IndemnityAgreementSignatureMethod.electronic]: "Electronic Signatures",
        [IndemnityAgreementSignatureMethod.physical]: "Wet Signatures",
    };
    function label(value) {
        return labelMapInternal[value];
    }
    IndemnityAgreementSignatureMethod.label = label;
    function options() {
        return indemnityAgreementSignatureMethodDef.map((v) => ({
            value: v,
            label: labelMapInternal[v],
        }));
    }
    IndemnityAgreementSignatureMethod.options = options;
})(IndemnityAgreementSignatureMethod || (IndemnityAgreementSignatureMethod = {}));
const indemnityStatusDef = ["pending", "review", "complete"];
const indemnityStatusSchema = z.enum(indemnityStatusDef);
const IndemnityStatus = indemnityStatusSchema.enum;
const nameSuffixDef = ["SR", "JR", "III", "IV", "V"];
const nameSuffixSchema = z.enum(nameSuffixDef);
const NameSuffix = nameSuffixSchema.enum;
const suretyAccountStatusDef = [
    "active",
    "closed",
    "declined",
    "review",
    "approved",
];
const suretyAccountStatusSchema = z.enum(suretyAccountStatusDef);
const SuretyAccountStatus = suretyAccountStatusSchema.enum;
const suretyAccountUnderwritingStatusDef = [
    "approved",
    "review",
    "declined",
    "processing",
];
const suretyAccountUnderwritingStatusSchema = z.enum(suretyAccountUnderwritingStatusDef);
const SuretyAccountUnderwritingStatus = suretyAccountUnderwritingStatusSchema.enum;
const suretyBondFormTemplateClaimResponseDef = [
    "meeting",
    "written",
    "corrective_action",
    "agree_to_pay",
    "other",
];
const suretyBondFormTemplateClaimResponseSchema = z.enum(suretyBondFormTemplateClaimResponseDef);
const SuretyBondFormTemplateClaimResponse = suretyBondFormTemplateClaimResponseSchema.enum;
const suretyBondFormTemplateDefaultTriggerDef = [
    "failure_to_sign_contract",
    "failure_to_provide_final_bond",
    "non_performance",
    "non_payment",
    "non_compliance",
    "declared_by_obligee",
    "other",
];
const suretyBondFormTemplateDefaultTriggerSchema = z.enum(suretyBondFormTemplateDefaultTriggerDef);
const SuretyBondFormTemplateDefaultTrigger = suretyBondFormTemplateDefaultTriggerSchema.enum;
const suretyBondFormToReviewStatusDef = [
    "approved",
    "declined",
    "review",
    "unsubmitted",
];
const suretyBondFormToReviewStatusSchema = z.enum(suretyBondFormToReviewStatusDef);
const SuretyBondFormToReviewStatus = suretyBondFormToReviewStatusSchema.enum;
const suretyBondFormTypeDef = ["bid", "pnp", "performance", "payment", "supply"];
const suretyBondFormTypeSchema = z.enum(suretyBondFormTypeDef);
const SuretyBondFormType = suretyBondFormTypeSchema.enum;
const contractSuretyBondFormTypeDef = [
    "bid",
    "pnp",
    "performance",
    "payment",
    "supply",
];
const contractSuretyBondFormTypeSchema = z.enum(contractSuretyBondFormTypeDef);
const ContractSuretyBondFormType = contractSuretyBondFormTypeSchema.enum;
const bidContractSuretyBondFormTypeDef = ["bid"];
const bidContractSuretyBondFormTypeSchema = z.enum(bidContractSuretyBondFormTypeDef);
const BidContractSuretyBondFormType = bidContractSuretyBondFormTypeSchema.enum;
const finalContractSuretyBondFormTypeDef = [
    "pnp",
    "performance",
    "payment",
    "supply",
];
const finalContractSuretyBondFormTypeSchema = z.enum(finalContractSuretyBondFormTypeDef);
const FinalContractSuretyBondFormType = finalContractSuretyBondFormTypeSchema.enum;
const bidBondAmountTypeDef = ["percent", "dollars"];
const bidBondAmountTypeSchema = z.enum(bidBondAmountTypeDef);
const BidBondAmountType = bidBondAmountTypeSchema.enum;
var SuretyBondIssuanceMethod;
(function (SuretyBondIssuanceMethod) {
    SuretyBondIssuanceMethod["electronic"] = "electronic";
    SuretyBondIssuanceMethod["physical"] = "physical";
    SuretyBondIssuanceMethod["agent"] = "agent";
})(SuretyBondIssuanceMethod || (SuretyBondIssuanceMethod = {}));
const suretyBondIssuanceMethodDef = [
    SuretyBondIssuanceMethod.electronic,
    SuretyBondIssuanceMethod.physical,
    SuretyBondIssuanceMethod.agent,
];
const suretyBondIssuanceMethodSchema = z.enum(suretyBondIssuanceMethodDef);
(function (SuretyBondIssuanceMethod) {
    const labelMapInternal = {
        [SuretyBondIssuanceMethod.electronic]: "Electronic",
        [SuretyBondIssuanceMethod.physical]: "Physical",
        [SuretyBondIssuanceMethod.agent]: "Agent",
    };
    function options() {
        return suretyBondIssuanceMethodDef.map((v) => ({
            value: v,
            label: labelMapInternal[v],
        }));
    }
    SuretyBondIssuanceMethod.options = options;
})(SuretyBondIssuanceMethod || (SuretyBondIssuanceMethod = {}));
const suretyBondRenewalMethodDef = ["certificate", "new_bond", "auto"];
const suretyBondRenewalMethodSchema = z.enum(suretyBondRenewalMethodDef);
const SuretyBondRenewalMethod = suretyBondRenewalMethodSchema.enum;
const suretyBondStatusDef = ["open", "closed"];
const suretyBondStatusSchema = z.enum(suretyBondStatusDef);
const SuretyBondStatus = suretyBondStatusSchema.enum;
const suretyBondTypeDef = ["bid", "final", "subdivision", "maintenance"];
const suretyBondTypeSchema = z.enum(suretyBondTypeDef);
const SuretyBondType = suretyBondTypeSchema.enum;
var SuretyBondTermAdjustmentType;
(function (SuretyBondTermAdjustmentType) {
    SuretyBondTermAdjustmentType["Base"] = "base";
    SuretyBondTermAdjustmentType["Adjustment"] = "adjustment";
})(SuretyBondTermAdjustmentType || (SuretyBondTermAdjustmentType = {}));
const suretyBondTermAdjustmentTypeDef = [
    SuretyBondTermAdjustmentType.Base,
    SuretyBondTermAdjustmentType.Adjustment,
];
const suretyBondTermAdjustmentTypeSchema = z.enum(suretyBondTermAdjustmentTypeDef);
(function (SuretyBondTermAdjustmentType) {
    const displayNames = {
        [SuretyBondTermAdjustmentType.Base]: "Base",
        [SuretyBondTermAdjustmentType.Adjustment]: "Adjustment",
    };
    function displayName(value) {
        return displayNames[value];
    }
    SuretyBondTermAdjustmentType.displayName = displayName;
    function values() {
        return suretyBondTermAdjustmentTypeDef;
    }
    SuretyBondTermAdjustmentType.values = values;
    function options() {
        return suretyBondTermAdjustmentTypeDef.map((v) => ({
            value: v,
            label: displayNames[v],
        }));
    }
    SuretyBondTermAdjustmentType.options = options;
})(SuretyBondTermAdjustmentType || (SuretyBondTermAdjustmentType = {}));
var SuretyBondTermPaymentStatus;
(function (SuretyBondTermPaymentStatus) {
    SuretyBondTermPaymentStatus["Paid"] = "paid";
    SuretyBondTermPaymentStatus["Unpaid"] = "unpaid";
    SuretyBondTermPaymentStatus["Waived"] = "waived";
})(SuretyBondTermPaymentStatus || (SuretyBondTermPaymentStatus = {}));
const suretyBondTermPaymentStatusDef = [
    SuretyBondTermPaymentStatus.Paid,
    SuretyBondTermPaymentStatus.Unpaid,
    SuretyBondTermPaymentStatus.Waived,
];
const suretyBondTermPaymentStatusSchema = z.enum(suretyBondTermPaymentStatusDef);
(function (SuretyBondTermPaymentStatus) {
    const displayNames = {
        [SuretyBondTermPaymentStatus.Paid]: "Paid",
        [SuretyBondTermPaymentStatus.Unpaid]: "Unpaid",
        [SuretyBondTermPaymentStatus.Waived]: "Waived",
    };
    function displayName(value) {
        return displayNames[value];
    }
    SuretyBondTermPaymentStatus.displayName = displayName;
    function values() {
        return suretyBondTermPaymentStatusDef;
    }
    SuretyBondTermPaymentStatus.values = values;
    function options() {
        return suretyBondTermPaymentStatusDef.map((v) => ({
            value: v,
            label: displayNames[v],
        }));
    }
    SuretyBondTermPaymentStatus.options = options;
    function rollup(statuses) {
        const normalized = statuses.map((status) => status === SuretyBondTermPaymentStatus.Waived ? SuretyBondTermPaymentStatus.Paid : status);
        if (normalized.length === 0 || normalized.includes(SuretyBondTermPaymentStatus.Unpaid)) {
            return SuretyBondTermPaymentStatus.Unpaid;
        }
        return SuretyBondTermPaymentStatus.Paid;
    }
    SuretyBondTermPaymentStatus.rollup = rollup;
})(SuretyBondTermPaymentStatus || (SuretyBondTermPaymentStatus = {}));
const suretyQuoteStatusDef = [
    "approved",
    "review",
    "declined",
    "accepted",
    "closed",
    "pending",
    "bound",
];
const suretyQuoteStatusSchema = z.enum(suretyQuoteStatusDef);
const SuretyQuoteStatus = suretyQuoteStatusSchema.enum;
const suretyQuoteUnderwritingStatusDef = [
    "approved",
    "review",
    "declined",
    "processing",
];
const suretyQuoteUnderwritingStatusSchema = z.enum(suretyQuoteUnderwritingStatusDef);
const SuretyQuoteUnderwritingStatus = suretyQuoteUnderwritingStatusSchema.enum;
const suretyQuoteTypeDef = [
    "bid",
    "bid_to_final",
    "final",
    "subdivision",
    "maintenance",
];
const suretyQuoteTypeSchema = z.enum(suretyQuoteTypeDef);
const SuretyQuoteType = suretyQuoteTypeSchema.enum;
const suretyTypeDef = ["contract", "compliance", "financial_guarantee"];
const suretyTypeSchema = z.enum(suretyTypeDef);
const SuretyType = suretyTypeSchema.enum;
const suretyTypeDisplayName = {
    contract: "Contract",
    compliance: "Compliance",
    financial_guarantee: "Financial Guarantee",
};
const contractSuretyTypeDef = ["bid", "bid_to_final", "final"];
const contractSuretyTypeSchema = z.enum(contractSuretyTypeDef);
const ContractSuretyType = contractSuretyTypeSchema.enum;
var ContractBondSubtype;
(function (ContractBondSubtype) {
    ContractBondSubtype["bid"] = "bid";
    ContractBondSubtype["final"] = "final";
})(ContractBondSubtype || (ContractBondSubtype = {}));
const contractBondSubtypeDef = [ContractBondSubtype.bid, ContractBondSubtype.final];
const contractBondSubtypeSchema = z.enum(contractBondSubtypeDef);
(function (ContractBondSubtype) {
    const labelMapInternal = {
        [ContractBondSubtype.bid]: "Bid",
        [ContractBondSubtype.final]: "Final",
    };
    function label(value) {
        return labelMapInternal[value];
    }
    ContractBondSubtype.label = label;
    function options() {
        return contractBondSubtypeDef.map((v) => ({
            value: v,
            label: labelMapInternal[v],
        }));
    }
    ContractBondSubtype.options = options;
})(ContractBondSubtype || (ContractBondSubtype = {}));
var ContractCategoryBondType;
(function (ContractCategoryBondType) {
    ContractCategoryBondType["contract_standard"] = "contract_standard";
    ContractCategoryBondType["contract_completion"] = "contract_completion";
    ContractCategoryBondType["contract_non_construction"] = "contract_non_construction";
})(ContractCategoryBondType || (ContractCategoryBondType = {}));
const contractCategoryBondTypeDef = [
    ContractCategoryBondType.contract_standard,
    ContractCategoryBondType.contract_completion,
    ContractCategoryBondType.contract_non_construction,
];
const contractCategoryBondTypeSchema = z.enum(contractCategoryBondTypeDef);
(function (ContractCategoryBondType) {
    const labelMapInternal = {
        [ContractCategoryBondType.contract_standard]: "Contract - Standard",
        [ContractCategoryBondType.contract_completion]: "Contract - Completion",
        [ContractCategoryBondType.contract_non_construction]: "Contract - Non-Construction",
    };
    function label(value) {
        return labelMapInternal[value];
    }
    ContractCategoryBondType.label = label;
    function options() {
        return contractCategoryBondTypeDef.map((v) => ({
            value: v,
            label: labelMapInternal[v],
        }));
    }
    ContractCategoryBondType.options = options;
})(ContractCategoryBondType || (ContractCategoryBondType = {}));
var CommercialSuretyBondType;
(function (CommercialSuretyBondType) {
    CommercialSuretyBondType["license_and_permit"] = "license_and_permit";
    CommercialSuretyBondType["court_fiduciary"] = "court_fiduciary";
    CommercialSuretyBondType["financial_guarantee"] = "financial_guarantee";
    CommercialSuretyBondType["credit_enhancement"] = "credit_enhancement";
    CommercialSuretyBondType["court_guarantee"] = "court_guarantee";
})(CommercialSuretyBondType || (CommercialSuretyBondType = {}));
const commercialSuretyBondTypeDef = [
    CommercialSuretyBondType.license_and_permit,
    CommercialSuretyBondType.court_fiduciary,
    CommercialSuretyBondType.financial_guarantee,
    CommercialSuretyBondType.credit_enhancement,
    CommercialSuretyBondType.court_guarantee,
];
const commercialSuretyBondTypeSchema = z.enum(commercialSuretyBondTypeDef);
(function (CommercialSuretyBondType) {
    const labelMapInternal = {
        [CommercialSuretyBondType.license_and_permit]: "License & Permit",
        [CommercialSuretyBondType.court_fiduciary]: "Court (Fiduciary)",
        [CommercialSuretyBondType.financial_guarantee]: "Financial Guarantee",
        [CommercialSuretyBondType.credit_enhancement]: "Credit Enhancement",
        [CommercialSuretyBondType.court_guarantee]: "Court (Guarantee)",
    };
    function label(value) {
        return labelMapInternal[value];
    }
    CommercialSuretyBondType.label = label;
    function options() {
        return commercialSuretyBondTypeDef.map((v) => ({
            value: v,
            label: labelMapInternal[v],
        }));
    }
    CommercialSuretyBondType.options = options;
})(CommercialSuretyBondType || (CommercialSuretyBondType = {}));
const suretyTypeOf = {
    contract_standard: "contract",
    contract_completion: "contract",
    contract_non_construction: "contract",
    license_and_permit: "compliance",
    court_fiduciary: "compliance",
    financial_guarantee: "financial_guarantee",
    credit_enhancement: "financial_guarantee",
    court_guarantee: "financial_guarantee",
};
const bondRequestBondTypeDef = [
    ...contractSuretyTypeDef,
    ...contractCategoryBondTypeDef,
    ...commercialSuretyBondTypeDef,
];
const bondRequestBondTypeSchema = z.enum(bondRequestBondTypeDef);
const closedReasonDef = [
    "lost_account",
    "placed_elsewhere",
    "not_needed",
    "stale",
    "declined",
    "other",
];
const closedReasonSchema = z.enum(closedReasonDef);
const ClosedReason = closedReasonSchema.enum;
const CLOSED_REASON_NOTE_MAX_LENGTH = 100;
const bidSuretyBondClosedReasonDef = [
    "bid_won",
    "bid_lost",
    "not_awarded",
    "returned_by_obligee",
    "input_error",
    "other",
];
const bidSuretyBondClosedReasonSchema = z.enum(bidSuretyBondClosedReasonDef);
const BidSuretyBondClosedReason = bidSuretyBondClosedReasonSchema.enum;
const companyFinancialStatementPreparationMethodDef = [
    "internal",
    "cpa_comp",
    "cpa_review",
    "cpa_audit",
    "does_not_keep",
];
const companyFinancialStatementPreparationMethodSchema = z.enum(companyFinancialStatementPreparationMethodDef);
const CompanyFinancialStatementPreparationMethod = companyFinancialStatementPreparationMethodSchema.enum;
const contractDamageDef = [
    "consequential",
    "prime_contract",
    "liquidated_gt_5000",
    "other",
    "none",
];
const contractDamageSchema = z.enum(contractDamageDef);
const ContractDamage = contractDamageSchema.enum;
const ContractDamageDescriptions = {
    consequential: "consequential damages",
    prime_contract: "prime contract damages",
    liquidated_gt_5000: "liquidated damages greater than $5,000",
    other: "an unknown damage provision",
    none: "none of the above damages",
};
const contractHazardDef = [
    "design_build",
    "environmental_or_remediation",
    "superfund",
    "efficiency_guarantees",
    "sovereign_nation_owner",
    "renewable",
    "takeover",
    "none",
];
const contractHazardSchema = z.enum(contractHazardDef);
const ContractHazard = contractHazardSchema.enum;
const ContractHazardDescriptions = {
    design_build: "design-build",
    environmental_or_remediation: "environmental or remediation work",
    superfund: "work at a superfund site",
    efficiency_guarantees: "efficiency guarantees",
    sovereign_nation_owner: "work for a sovereign nation owner",
    renewable: "renewable language",
    takeover: "the takeover of an existing contract",
    none: "none of the above hazards",
};
const contactTypeDef = ["individual", "company", "fund"];
const contactTypeSchema = z.enum(contactTypeDef);
const ContactType = contactTypeSchema.enum;
const contractSuretyAccountUnderwritingModelSizeDef = ["small", "medium", "large"];
const contractSuretyAccountUnderwritingModelSizeSchema = z.enum(contractSuretyAccountUnderwritingModelSizeDef);
const ContractSuretyAccountUnderwritingModelSize = contractSuretyAccountUnderwritingModelSizeSchema.enum;
const finalContractSuretyBondClosedReasonDef = [
    "project_completed",
    "expired",
    "non_payment",
    "replaced",
    "input_error",
    "returned_by_obligee",
    "other",
];
const finalContractSuretyBondClosedReasonSchema = z.enum(finalContractSuretyBondClosedReasonDef);
const FinalContractSuretyBondClosedReason = finalContractSuretyBondClosedReasonSchema.enum;
const contractSuretyBondTypeDef = ["pnp", "performance", "payment", "supply"];
const contractSuretyBondTypeSchema = z.enum(contractSuretyBondTypeDef);
const ContractSuretyBondType = contractSuretyBondTypeSchema.enum;
const eSignatureMethodDef = ["docusign", "notarize"];
const eSignatureMethodSchema = z.enum(eSignatureMethodDef);
const ESignatureMethod = eSignatureMethodSchema.enum;
const eSignatureStatusDef = ["sent", "completed", "declined", "voided"];
const eSignatureStatusSchema = z.enum(eSignatureStatusDef);
const ESignatureStatus = eSignatureStatusSchema.enum;
const eSignatureVendorDef = ["docusign", "notarize"];
const eSignatureVendorSchema = z.enum(eSignatureVendorDef);
const ESignatureVendor = eSignatureVendorSchema.enum;
const wordBinaryFormatsDef = ["doc"];
const wordXMLFormatsDef = ["docx", "docm"];
const wordFileExtensionDef = [...wordBinaryFormatsDef, ...wordXMLFormatsDef];
const wordFileExtensionSchema = z.enum(wordFileExtensionDef);
const WordFileExtension = wordFileExtensionSchema.enum;
const excelBinaryFormatsDef = ["xla", "xls", "xlsb"];
const excelXMLFormatsDef = ["xlam", "xlsx", "xlsm"];
const excelFileExtensionDef = [...excelBinaryFormatsDef, ...excelXMLFormatsDef];
const excelFileExtensionSchema = z.enum(excelFileExtensionDef);
const ExcelFileExtension = excelFileExtensionSchema.enum;
const powerPointBinaryFormatsDef = ["ppt", "pps"];
const powerPointXMLFormatsDef = ["ppsx", "pptx", "pptm", "ppsm"];
const powerPointFileExtensionDef = [
    ...powerPointBinaryFormatsDef,
    ...powerPointXMLFormatsDef,
];
const powerPointFileExtensionSchema = z.enum(powerPointFileExtensionDef);
const PowerPointFileExtension = powerPointFileExtensionSchema.enum;
const msBinaryFormatsDef = [
    ...wordBinaryFormatsDef,
    ...excelBinaryFormatsDef,
    ...powerPointBinaryFormatsDef,
];
const msXMLFormatsDef = [
    ...wordXMLFormatsDef,
    ...excelXMLFormatsDef,
    ...powerPointXMLFormatsDef,
];
const msFileExtensionDef = [
    ...wordFileExtensionDef,
    ...excelFileExtensionDef,
    ...powerPointFileExtensionDef,
];
const adobeFileExtensionDef = ["pdf"];
const isoFileExtensionDef = ["png", "json", "csv", "gif", "jpeg", "jpg"];
const emailFileExtensionDef = ["msg", "eml"];
const emailFileExtensionSchema = z.enum(emailFileExtensionDef);
const EmailFileExtension = emailFileExtensionSchema.enum;
const fileExtensionDef = [
    ...adobeFileExtensionDef,
    ...isoFileExtensionDef,
    ...msFileExtensionDef,
    ...emailFileExtensionDef,
];
const fileExtensionSchema = z.enum(fileExtensionDef);
const FileExtension = fileExtensionSchema.enum;
const adminUserRoleDef = ["admin-v2", "underwriter", "developer"];
const adminUserRoleSchema = z.enum(adminUserRoleDef);
const AdminUserRole = adminUserRoleSchema.enum;
const agentUserRoleDef = ["agent", "agent-admin"];
const agentUserRoleSchema = z.enum(agentUserRoleDef);
const AgentUserRole = agentUserRoleSchema.enum;
const auth0UserRoleDef = [...agentUserRoleDef, ...adminUserRoleDef];
const auth0UserRoleSchema = z.enum(auth0UserRoleDef);
const Auth0UserRole = auth0UserRoleSchema.enum;
const usStateOrTerritoryDef = [
    "AL",
    "AK",
    "AZ",
    "AR",
    "CA",
    "CO",
    "CT",
    "DE",
    "DC",
    "FL",
    "GA",
    "HI",
    "ID",
    "IL",
    "IN",
    "IA",
    "KS",
    "KY",
    "LA",
    "ME",
    "MD",
    "MA",
    "MI",
    "MN",
    "MS",
    "MO",
    "MT",
    "NE",
    "NV",
    "NH",
    "NJ",
    "NM",
    "NY",
    "NC",
    "ND",
    "OH",
    "OK",
    "OR",
    "PA",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "UT",
    "VT",
    "VA",
    "WA",
    "WV",
    "WI",
    "WY",
];
const usStateOrTerritorySchema = z.enum(usStateOrTerritoryDef);
const USStateOrTerritory = usStateOrTerritorySchema.enum;
const eSignatureDocumentTypeDef = ["contract_surety_indemnity_agreement"];
const eSignatureDocumentTypeSchema = z.enum(eSignatureDocumentTypeDef);
const ESignatureDocumentType = eSignatureDocumentTypeSchema.enum;
const dateTimeSchema = z.preprocess((val) => {
    if (val instanceof Date) {
        return DateTime.fromJSDate(val).toUTC();
    }
    if (typeof val === "string") {
        return DateTime.fromISO(val).toUTC();
    }
    if (DateTime.isDateTime(val)) {
        return val.toUTC();
    }
    return val;
}, z.custom((data) => DateTime.isDateTime(data) && data.isValid));
const exposureSizeSchema = z.enum(["large", "medium", "small"]);
const ExposureSize = exposureSizeSchema.enum;
const featureFlagNameSchema = z.enum([
    "cloud_storage",
    "credit_reporting",
    "docusign",
    "email",
    "fein_validation",
    "ofac_check",
    "pdf_filling",
    "slack_notifications",
    "ai_extraction",
    "ai_financial_extraction",
    "ai_classification_extraction",
    "ai_contract_extraction",
    "ai_bond_request_extraction",
    "ai_application_extraction",
    "sse_enabled",
    "bond_request_extraction",
    "account_auto_approval",
    "bond_request_auto_approval",
    "wip_extraction_project",
    "ai_wip_extraction",
    "agent_commercial_experience",
    "commercial_surety",
    "experian_company_credit",
    "experian_live_company_credit",
    "experian_individual_credit",
    "experian_live_individual_credit",
    "surety_type_bonding_lines",
]);
const FeatureFlagName = featureFlagNameSchema.enum;
var FinancialPeriodPreparationMethod;
(function (FinancialPeriodPreparationMethod) {
    FinancialPeriodPreparationMethod["Audit"] = "audit";
    FinancialPeriodPreparationMethod["Review"] = "review";
    FinancialPeriodPreparationMethod["Compilation"] = "compilation";
    FinancialPeriodPreparationMethod["Internal"] = "internal";
})(FinancialPeriodPreparationMethod || (FinancialPeriodPreparationMethod = {}));
const financialPeriodPreparationMethodDef = [
    FinancialPeriodPreparationMethod.Audit,
    FinancialPeriodPreparationMethod.Review,
    FinancialPeriodPreparationMethod.Compilation,
    FinancialPeriodPreparationMethod.Internal,
];
const financialPeriodPreparationMethodSchema = z.enum(financialPeriodPreparationMethodDef);
(function (FinancialPeriodPreparationMethod) {
    const displayNames = {
        [FinancialPeriodPreparationMethod.Audit]: "Audit",
        [FinancialPeriodPreparationMethod.Review]: "Review",
        [FinancialPeriodPreparationMethod.Compilation]: "Compilation",
        [FinancialPeriodPreparationMethod.Internal]: "Internal",
    };
    function displayName(value) {
        return displayNames[value];
    }
    FinancialPeriodPreparationMethod.displayName = displayName;
    function values() {
        return financialPeriodPreparationMethodDef;
    }
    FinancialPeriodPreparationMethod.values = values;
    function options() {
        return financialPeriodPreparationMethodDef.map((v) => ({
            value: v,
            label: displayNames[v],
        }));
    }
    FinancialPeriodPreparationMethod.options = options;
    const validValues = new Set(financialPeriodPreparationMethodDef);
    function normalize(source) {
        if (!source)
            return null;
        const normalized = source.toLowerCase();
        if (validValues.has(normalized))
            return normalized;
        return null;
    }
    FinancialPeriodPreparationMethod.normalize = normalize;
    function qualityRank(value) {
        return value
            ? financialPeriodPreparationMethodDef.indexOf(value)
            : financialPeriodPreparationMethodDef.length;
    }
    FinancialPeriodPreparationMethod.qualityRank = qualityRank;
})(FinancialPeriodPreparationMethod || (FinancialPeriodPreparationMethod = {}));
var PeriodType;
(function (PeriodType) {
    PeriodType["Fye"] = "fye";
    PeriodType["Ttm"] = "ttm";
    PeriodType["Ytd"] = "ytd";
    PeriodType["Qtd"] = "qtd";
    PeriodType["Other"] = "other";
})(PeriodType || (PeriodType = {}));
const periodTypeDef = [
    PeriodType.Fye,
    PeriodType.Ttm,
    PeriodType.Ytd,
    PeriodType.Qtd,
    PeriodType.Other,
];
(function (PeriodType) {
    const displayNames = {
        [PeriodType.Fye]: "FYE",
        [PeriodType.Ttm]: "TTM",
        [PeriodType.Ytd]: "YTD",
        [PeriodType.Qtd]: "QTD",
        [PeriodType.Other]: "Other",
    };
    function displayName(value) {
        return displayNames[value];
    }
    PeriodType.displayName = displayName;
    function values() {
        return periodTypeDef;
    }
    PeriodType.values = values;
    function options() {
        return periodTypeDef.map((v) => ({ value: v, label: displayNames[v] }));
    }
    PeriodType.options = options;
    const validValues = new Set(periodTypeDef);
    function normalize(source) {
        if (!source)
            return PeriodType.Other;
        const normalized = source.toLowerCase();
        if (validValues.has(normalized))
            return normalized;
        return PeriodType.Other;
    }
    PeriodType.normalize = normalize;
    function rank(value) {
        return value ? periodTypeDef.indexOf(value) : periodTypeDef.length;
    }
    PeriodType.rank = rank;
})(PeriodType || (PeriodType = {}));
const periodTypeSchema = z.enum(periodTypeDef);
var StatementType;
(function (StatementType) {
    StatementType["Fye"] = "fye";
    StatementType["Interim"] = "interim";
})(StatementType || (StatementType = {}));
const statementTypeDef = [StatementType.Fye, StatementType.Interim];
(function (StatementType) {
    const displayNames = {
        [StatementType.Fye]: "Fiscal Year End",
        [StatementType.Interim]: "Interim",
    };
    function displayName(value) {
        return displayNames[value];
    }
    StatementType.displayName = displayName;
    function values() {
        return statementTypeDef;
    }
    StatementType.values = values;
    function options() {
        return statementTypeDef.map((v) => ({ value: v, label: displayNames[v] }));
    }
    StatementType.options = options;
    function fromPeriodType(value) {
        return value === PeriodType.Fye ? StatementType.Fye : StatementType.Interim;
    }
    StatementType.fromPeriodType = fromPeriodType;
})(StatementType || (StatementType = {}));
const statementTypeSchema = z.enum(statementTypeDef);
const accountingStandardDef = ["GAAP", "GAAS", "IFRS", "Other", "None"];
const accountingStandardSchema = z.enum(accountingStandardDef);
const AccountingStandard = accountingStandardSchema.enum;
const revenueRecognitionDef = [
    "% Complete",
    "Completed Contract",
    "Accrual",
    "Cash",
    "Multiple",
];
const revenueRecognitionSchema = z.enum(revenueRecognitionDef);
const RevenueRecognition = revenueRecognitionSchema.enum;
var ExtractionStatus;
(function (ExtractionStatus) {
    ExtractionStatus["NotStarted"] = "not_started";
    ExtractionStatus["Pending"] = "pending";
    ExtractionStatus["Completed"] = "completed";
    ExtractionStatus["Failed"] = "failed";
    ExtractionStatus["Partial"] = "partial";
})(ExtractionStatus || (ExtractionStatus = {}));
const extractionStatusDef = [
    ExtractionStatus.NotStarted,
    ExtractionStatus.Pending,
    ExtractionStatus.Completed,
    ExtractionStatus.Failed,
    ExtractionStatus.Partial,
];
(function (ExtractionStatus) {
    const displayNames = {
        [ExtractionStatus.NotStarted]: "Not Started",
        [ExtractionStatus.Pending]: "Pending",
        [ExtractionStatus.Completed]: "Completed",
        [ExtractionStatus.Failed]: "Failed",
        [ExtractionStatus.Partial]: "Partial",
    };
    function displayName(value) {
        return displayNames[value];
    }
    ExtractionStatus.displayName = displayName;
    function values() {
        return extractionStatusDef;
    }
    ExtractionStatus.values = values;
    function options() {
        return extractionStatusDef.map((v) => ({
            value: v,
            label: displayNames[v],
        }));
    }
    ExtractionStatus.options = options;
    const validValues = new Set(extractionStatusDef);
    function normalize(source) {
        if (!source)
            return ExtractionStatus.NotStarted;
        const normalized = source.toLowerCase();
        if (validValues.has(normalized))
            return normalized;
        return ExtractionStatus.NotStarted;
    }
    ExtractionStatus.normalize = normalize;
})(ExtractionStatus || (ExtractionStatus = {}));
const extractionStatusSchema = z.enum(extractionStatusDef);
var WipExtractionReviewStatus;
(function (WipExtractionReviewStatus) {
    WipExtractionReviewStatus["NotStarted"] = "not_started";
    WipExtractionReviewStatus["Pending"] = "pending";
    WipExtractionReviewStatus["Failed"] = "failed";
    WipExtractionReviewStatus["Reviewed"] = "reviewed";
    WipExtractionReviewStatus["NeedsReview"] = "needs_review";
    WipExtractionReviewStatus["HighConfidence"] = "high_confidence";
})(WipExtractionReviewStatus || (WipExtractionReviewStatus = {}));
const wipExtractionReviewStatusDef = [
    WipExtractionReviewStatus.NotStarted,
    WipExtractionReviewStatus.Pending,
    WipExtractionReviewStatus.Failed,
    WipExtractionReviewStatus.Reviewed,
    WipExtractionReviewStatus.NeedsReview,
    WipExtractionReviewStatus.HighConfidence,
];
(function (WipExtractionReviewStatus) {
    const displayNames = {
        [WipExtractionReviewStatus.NotStarted]: "Not Started",
        [WipExtractionReviewStatus.Pending]: "Pending",
        [WipExtractionReviewStatus.Failed]: "Failed",
        [WipExtractionReviewStatus.Reviewed]: "Reviewed",
        [WipExtractionReviewStatus.NeedsReview]: "Needs Review",
        [WipExtractionReviewStatus.HighConfidence]: "High Confidence",
    };
    function displayName(value) {
        return displayNames[value];
    }
    WipExtractionReviewStatus.displayName = displayName;
    function values() {
        return wipExtractionReviewStatusDef;
    }
    WipExtractionReviewStatus.values = values;
    function options() {
        return wipExtractionReviewStatusDef.map((v) => ({
            value: v,
            label: displayNames[v],
        }));
    }
    WipExtractionReviewStatus.options = options;
})(WipExtractionReviewStatus || (WipExtractionReviewStatus = {}));
const wipExtractionReviewStatusSchema = z.enum(wipExtractionReviewStatusDef);
var AttachmentSource;
(function (AttachmentSource) {
    AttachmentSource["InitialSubmission"] = "initial_submission";
    AttachmentSource["PostSubmission"] = "post_submission";
    AttachmentSource["Account"] = "account";
    AttachmentSource["Bond"] = "bond";
    AttachmentSource["Quote"] = "quote";
    AttachmentSource["WorkInProgressStatement"] = "work_in_progress_statement";
})(AttachmentSource || (AttachmentSource = {}));
const attachmentSourceDef = [
    AttachmentSource.InitialSubmission,
    AttachmentSource.PostSubmission,
    AttachmentSource.Account,
    AttachmentSource.Bond,
    AttachmentSource.Quote,
    AttachmentSource.WorkInProgressStatement,
];
const attachmentSourceSchema = z.enum(attachmentSourceDef);
(function (AttachmentSource) {
    const displayNames = {
        [AttachmentSource.InitialSubmission]: "Initial Submission",
        [AttachmentSource.PostSubmission]: "Post Submission",
        [AttachmentSource.Account]: "Account",
        [AttachmentSource.Bond]: "Bond",
        [AttachmentSource.Quote]: "Quote",
        [AttachmentSource.WorkInProgressStatement]: "Work In Progress Statement",
    };
    function displayName(value) {
        return displayNames[value];
    }
    AttachmentSource.displayName = displayName;
    function values() {
        return attachmentSourceDef;
    }
    AttachmentSource.values = values;
    function options() {
        return attachmentSourceDef.map((v) => ({ value: v, label: displayNames[v] }));
    }
    AttachmentSource.options = options;
})(AttachmentSource || (AttachmentSource = {}));
const componentExtractionStatusDef = ["extracted", "not_found", "failed"];
const componentExtractionStatusSchema = z.enum(componentExtractionStatusDef);
const ComponentExtractionStatus = componentExtractionStatusSchema.enum;
const financialStatementComponentDef = ["balance_sheet", "income_statement"];
const financialStatementComponentSchema = z.enum(financialStatementComponentDef);
const FinancialStatementComponent = financialStatementComponentSchema.enum;
const fileClassificationStatusDef = [
    "not_started",
    "pending",
    "completed",
    "failed",
];
const fileClassificationStatusSchema = z.enum(fileClassificationStatusDef);
const FileClassificationStatus = fileClassificationStatusSchema.enum;
var EmailSubmissionStatus;
(function (EmailSubmissionStatus) {
    EmailSubmissionStatus["Processing"] = "processing";
    EmailSubmissionStatus["NeedsReview"] = "needs_review";
    EmailSubmissionStatus["ReadyToSubmit"] = "ready_to_submit";
    EmailSubmissionStatus["Submitted"] = "submitted";
})(EmailSubmissionStatus || (EmailSubmissionStatus = {}));
const emailSubmissionStatusDef = [
    EmailSubmissionStatus.Processing,
    EmailSubmissionStatus.NeedsReview,
    EmailSubmissionStatus.ReadyToSubmit,
    EmailSubmissionStatus.Submitted,
];
const emailSubmissionStatusSchema = z.enum(emailSubmissionStatusDef);
(function (EmailSubmissionStatus) {
    const displayNames = {
        [EmailSubmissionStatus.Processing]: "Processing",
        [EmailSubmissionStatus.NeedsReview]: "Needs Review",
        [EmailSubmissionStatus.ReadyToSubmit]: "Ready to Submit",
        [EmailSubmissionStatus.Submitted]: "Submitted",
    };
    function displayName(value) {
        return displayNames[value];
    }
    EmailSubmissionStatus.displayName = displayName;
    function values() {
        return emailSubmissionStatusDef;
    }
    EmailSubmissionStatus.values = values;
    function options() {
        return emailSubmissionStatusDef.map((v) => ({ value: v, label: displayNames[v] }));
    }
    EmailSubmissionStatus.options = options;
})(EmailSubmissionStatus || (EmailSubmissionStatus = {}));
var EmailForwardParseMethod;
(function (EmailForwardParseMethod) {
    EmailForwardParseMethod["EmlAttachment"] = "eml_attachment";
    EmailForwardParseMethod["HtmlGmail"] = "html_gmail";
    EmailForwardParseMethod["HtmlOutlook"] = "html_outlook";
    EmailForwardParseMethod["TextGmail"] = "text_gmail";
    EmailForwardParseMethod["TextOutlook"] = "text_outlook";
    EmailForwardParseMethod["TextApple"] = "text_apple";
    EmailForwardParseMethod["TextReplyQuote"] = "text_reply_quote";
    EmailForwardParseMethod["Unparsed"] = "unparsed";
})(EmailForwardParseMethod || (EmailForwardParseMethod = {}));
const emailForwardParseMethodDef = [
    EmailForwardParseMethod.EmlAttachment,
    EmailForwardParseMethod.HtmlGmail,
    EmailForwardParseMethod.HtmlOutlook,
    EmailForwardParseMethod.TextGmail,
    EmailForwardParseMethod.TextOutlook,
    EmailForwardParseMethod.TextApple,
    EmailForwardParseMethod.TextReplyQuote,
    EmailForwardParseMethod.Unparsed,
];
const emailForwardParseMethodSchema = z.enum(emailForwardParseMethodDef);
(function (EmailForwardParseMethod) {
    const displayNames = {
        [EmailForwardParseMethod.EmlAttachment]: "EML Attachment",
        [EmailForwardParseMethod.HtmlGmail]: "HTML (Gmail)",
        [EmailForwardParseMethod.HtmlOutlook]: "HTML (Outlook)",
        [EmailForwardParseMethod.TextGmail]: "Text (Gmail)",
        [EmailForwardParseMethod.TextOutlook]: "Text (Outlook)",
        [EmailForwardParseMethod.TextApple]: "Text (Apple Mail)",
        [EmailForwardParseMethod.TextReplyQuote]: "Text (Reply Quote)",
        [EmailForwardParseMethod.Unparsed]: "Unparsed",
    };
    function displayName(value) {
        return displayNames[value];
    }
    EmailForwardParseMethod.displayName = displayName;
    function values() {
        return emailForwardParseMethodDef;
    }
    EmailForwardParseMethod.values = values;
    function options() {
        return emailForwardParseMethodDef.map((v) => ({ value: v, label: displayNames[v] }));
    }
    EmailForwardParseMethod.options = options;
})(EmailForwardParseMethod || (EmailForwardParseMethod = {}));
var EmailSubmissionAttachmentCascadeStatus;
(function (EmailSubmissionAttachmentCascadeStatus) {
    EmailSubmissionAttachmentCascadeStatus["NoInScopePdf"] = "no_in_scope_pdf";
    EmailSubmissionAttachmentCascadeStatus["Complete"] = "complete";
    EmailSubmissionAttachmentCascadeStatus["Partial"] = "partial";
    EmailSubmissionAttachmentCascadeStatus["WeakPicksExhausted"] = "weak_picks_exhausted";
})(EmailSubmissionAttachmentCascadeStatus || (EmailSubmissionAttachmentCascadeStatus = {}));
const emailSubmissionAttachmentCascadeStatusDef = [
    EmailSubmissionAttachmentCascadeStatus.NoInScopePdf,
    EmailSubmissionAttachmentCascadeStatus.Complete,
    EmailSubmissionAttachmentCascadeStatus.Partial,
    EmailSubmissionAttachmentCascadeStatus.WeakPicksExhausted,
];
const emailSubmissionAttachmentCascadeStatusSchema = z.enum(emailSubmissionAttachmentCascadeStatusDef);
(function (EmailSubmissionAttachmentCascadeStatus) {
    const displayNames = {
        [EmailSubmissionAttachmentCascadeStatus.NoInScopePdf]: "No In-Scope Attachment",
        [EmailSubmissionAttachmentCascadeStatus.Complete]: "Complete",
        [EmailSubmissionAttachmentCascadeStatus.Partial]: "Partial",
        [EmailSubmissionAttachmentCascadeStatus.WeakPicksExhausted]: "Weak Picks Exhausted",
    };
    function displayName(value) {
        return displayNames[value];
    }
    EmailSubmissionAttachmentCascadeStatus.displayName = displayName;
    function values() {
        return emailSubmissionAttachmentCascadeStatusDef;
    }
    EmailSubmissionAttachmentCascadeStatus.values = values;
    function options() {
        return emailSubmissionAttachmentCascadeStatusDef.map((v) => ({
            value: v,
            label: displayNames[v],
        }));
    }
    EmailSubmissionAttachmentCascadeStatus.options = options;
})(EmailSubmissionAttachmentCascadeStatus || (EmailSubmissionAttachmentCascadeStatus = {}));
var EmailSubmissionConversionStatus;
(function (EmailSubmissionConversionStatus) {
    EmailSubmissionConversionStatus["Created"] = "created";
    EmailSubmissionConversionStatus["HeldForReview"] = "held_for_review";
})(EmailSubmissionConversionStatus || (EmailSubmissionConversionStatus = {}));
const emailSubmissionConversionStatusDef = [
    EmailSubmissionConversionStatus.Created,
    EmailSubmissionConversionStatus.HeldForReview,
];
const emailSubmissionConversionStatusSchema = z.enum(emailSubmissionConversionStatusDef);
(function (EmailSubmissionConversionStatus) {
    const displayNames = {
        [EmailSubmissionConversionStatus.Created]: "Created",
        [EmailSubmissionConversionStatus.HeldForReview]: "Held for Review",
    };
    function displayName(value) {
        return displayNames[value];
    }
    EmailSubmissionConversionStatus.displayName = displayName;
    function values() {
        return emailSubmissionConversionStatusDef;
    }
    EmailSubmissionConversionStatus.values = values;
    function options() {
        return emailSubmissionConversionStatusDef.map((v) => ({ value: v, label: displayNames[v] }));
    }
    EmailSubmissionConversionStatus.options = options;
})(EmailSubmissionConversionStatus || (EmailSubmissionConversionStatus = {}));
const dataSourceDef = ["manual", "extraction"];
const dataSourceSchema = z.enum(dataSourceDef);
const DataSource = dataSourceSchema.enum;
const extractionErrorTypeDef = [
    "PDF_CORRUPT",
    "PDF_NOT_FINANCIAL",
    "EXTRACTION_TIMEOUT",
    "LLM_ERROR",
    "COMPONENT_NOT_FOUND",
    "UNKNOWN",
];
const extractionErrorTypeSchema = z.enum(extractionErrorTypeDef);
const ExtractionErrorType = extractionErrorTypeSchema.enum;
const expectedExtractionErrorTypeDef = [ExtractionErrorType.PDF_NOT_FINANCIAL];
function isExpectedExtractionErrorType(errorType) {
    return expectedExtractionErrorTypeDef.includes(errorType);
}
var NaicsCodeKind;
(function (NaicsCodeKind) {
    NaicsCodeKind["sector"] = "sector";
    NaicsCodeKind["subsector"] = "subsector";
    NaicsCodeKind["industry_group"] = "industry_group";
    NaicsCodeKind["naics_industry"] = "naics_industry";
    NaicsCodeKind["national_industry"] = "national_industry";
    NaicsCodeKind["detail_item"] = "detail_item";
})(NaicsCodeKind || (NaicsCodeKind = {}));
const naicsCodeKindDef = [
    NaicsCodeKind.sector,
    NaicsCodeKind.subsector,
    NaicsCodeKind.industry_group,
    NaicsCodeKind.naics_industry,
    NaicsCodeKind.national_industry,
    NaicsCodeKind.detail_item,
];
const naicsCodeKindSchema = z.enum(naicsCodeKindDef);
(function (NaicsCodeKind) {
    const displayNames = {
        [NaicsCodeKind.sector]: "Sector",
        [NaicsCodeKind.subsector]: "Subsector",
        [NaicsCodeKind.industry_group]: "Industry Group",
        [NaicsCodeKind.naics_industry]: "NAICS Industry",
        [NaicsCodeKind.national_industry]: "National Industry",
        [NaicsCodeKind.detail_item]: "Detail Item",
    };
    function displayName(value) {
        return displayNames[value];
    }
    NaicsCodeKind.displayName = displayName;
    function values() {
        return naicsCodeKindDef;
    }
    NaicsCodeKind.values = values;
    function options() {
        return naicsCodeKindDef.map((v) => ({ value: v, label: displayNames[v] }));
    }
    NaicsCodeKind.options = options;
})(NaicsCodeKind || (NaicsCodeKind = {}));
var SfaaClassExperienceImportStatus;
(function (SfaaClassExperienceImportStatus) {
    SfaaClassExperienceImportStatus["Pending"] = "pending";
    SfaaClassExperienceImportStatus["Processing"] = "processing";
    SfaaClassExperienceImportStatus["Succeeded"] = "succeeded";
    SfaaClassExperienceImportStatus["Failed"] = "failed";
})(SfaaClassExperienceImportStatus || (SfaaClassExperienceImportStatus = {}));
const sfaaClassExperienceImportStatusDef = [
    SfaaClassExperienceImportStatus.Pending,
    SfaaClassExperienceImportStatus.Processing,
    SfaaClassExperienceImportStatus.Succeeded,
    SfaaClassExperienceImportStatus.Failed,
];
const sfaaClassExperienceImportStatusSchema = z.enum(sfaaClassExperienceImportStatusDef);
(function (SfaaClassExperienceImportStatus) {
    const displayNames = {
        [SfaaClassExperienceImportStatus.Pending]: "Pending",
        [SfaaClassExperienceImportStatus.Processing]: "Processing",
        [SfaaClassExperienceImportStatus.Succeeded]: "Succeeded",
        [SfaaClassExperienceImportStatus.Failed]: "Failed",
    };
    function displayName(value) {
        return displayNames[value];
    }
    SfaaClassExperienceImportStatus.displayName = displayName;
    function values() {
        return sfaaClassExperienceImportStatusDef;
    }
    SfaaClassExperienceImportStatus.values = values;
    function options() {
        return sfaaClassExperienceImportStatusDef.map((v) => ({
            value: v,
            label: displayNames[v],
        }));
    }
    SfaaClassExperienceImportStatus.options = options;
})(SfaaClassExperienceImportStatus || (SfaaClassExperienceImportStatus = {}));
var FredSeriesGroup;
(function (FredSeriesGroup) {
    FredSeriesGroup["RatesAndCredit"] = "rates_and_credit";
    FredSeriesGroup["LaborAndActivity"] = "labor_and_activity";
    FredSeriesGroup["PricesAndInputCosts"] = "prices_and_input_costs";
})(FredSeriesGroup || (FredSeriesGroup = {}));
const fredSeriesGroupDef = [
    FredSeriesGroup.RatesAndCredit,
    FredSeriesGroup.LaborAndActivity,
    FredSeriesGroup.PricesAndInputCosts,
];
const fredSeriesGroupSchema = z.enum(fredSeriesGroupDef);
(function (FredSeriesGroup) {
    const displayNames = {
        [FredSeriesGroup.RatesAndCredit]: "Rates and credit conditions",
        [FredSeriesGroup.LaborAndActivity]: "Labor and activity",
        [FredSeriesGroup.PricesAndInputCosts]: "Prices and input costs",
    };
    function displayName(value) {
        return displayNames[value];
    }
    FredSeriesGroup.displayName = displayName;
    function values() {
        return fredSeriesGroupDef;
    }
    FredSeriesGroup.values = values;
    function options() {
        return fredSeriesGroupDef.map((v) => ({ value: v, label: displayNames[v] }));
    }
    FredSeriesGroup.options = options;
})(FredSeriesGroup || (FredSeriesGroup = {}));
var FredFrequency;
(function (FredFrequency) {
    FredFrequency["Daily"] = "daily";
    FredFrequency["Weekly"] = "weekly";
    FredFrequency["Monthly"] = "monthly";
    FredFrequency["Quarterly"] = "quarterly";
    FredFrequency["Annual"] = "annual";
})(FredFrequency || (FredFrequency = {}));
const fredFrequencyDef = [
    FredFrequency.Daily,
    FredFrequency.Weekly,
    FredFrequency.Monthly,
    FredFrequency.Quarterly,
    FredFrequency.Annual,
];
const fredFrequencySchema = z.enum(fredFrequencyDef);
(function (FredFrequency) {
    const displayNames = {
        [FredFrequency.Daily]: "Daily",
        [FredFrequency.Weekly]: "Weekly",
        [FredFrequency.Monthly]: "Monthly",
        [FredFrequency.Quarterly]: "Quarterly",
        [FredFrequency.Annual]: "Annual",
    };
    function displayName(value) {
        return displayNames[value];
    }
    FredFrequency.displayName = displayName;
    function values() {
        return fredFrequencyDef;
    }
    FredFrequency.values = values;
    function options() {
        return fredFrequencyDef.map((v) => ({ value: v, label: displayNames[v] }));
    }
    FredFrequency.options = options;
})(FredFrequency || (FredFrequency = {}));
var FredSeasonalAdjustment;
(function (FredSeasonalAdjustment) {
    FredSeasonalAdjustment["SeasonallyAdjusted"] = "seasonally_adjusted";
    FredSeasonalAdjustment["NotSeasonallyAdjusted"] = "not_seasonally_adjusted";
    FredSeasonalAdjustment["SeasonallyAdjustedAnnualRate"] = "seasonally_adjusted_annual_rate";
})(FredSeasonalAdjustment || (FredSeasonalAdjustment = {}));
const fredSeasonalAdjustmentDef = [
    FredSeasonalAdjustment.SeasonallyAdjusted,
    FredSeasonalAdjustment.NotSeasonallyAdjusted,
    FredSeasonalAdjustment.SeasonallyAdjustedAnnualRate,
];
const fredSeasonalAdjustmentSchema = z.enum(fredSeasonalAdjustmentDef);
(function (FredSeasonalAdjustment) {
    const displayNames = {
        [FredSeasonalAdjustment.SeasonallyAdjusted]: "SA",
        [FredSeasonalAdjustment.NotSeasonallyAdjusted]: "NSA",
        [FredSeasonalAdjustment.SeasonallyAdjustedAnnualRate]: "SAAR",
    };
    function displayName(value) {
        return displayNames[value];
    }
    FredSeasonalAdjustment.displayName = displayName;
    function values() {
        return fredSeasonalAdjustmentDef;
    }
    FredSeasonalAdjustment.values = values;
    function options() {
        return fredSeasonalAdjustmentDef.map((v) => ({ value: v, label: displayNames[v] }));
    }
    FredSeasonalAdjustment.options = options;
})(FredSeasonalAdjustment || (FredSeasonalAdjustment = {}));
var FredScale;
(function (FredScale) {
    FredScale["Percent"] = "percent";
    FredScale["NetPercentOfBanks"] = "net_percent_of_banks";
    FredScale["PercentOfBalances"] = "percent_of_balances";
    FredScale["PercentOfFirms"] = "percent_of_firms";
    FredScale["ThousandsOfPersons"] = "thousands_of_persons";
    FredScale["MillionsOfDollarsSaar"] = "millions_of_dollars_saar";
    FredScale["MillionsOfDollars"] = "millions_of_dollars";
    FredScale["MillionsOfChained2017Dollars"] = "millions_of_chained_2017_dollars";
    FredScale["ThousandsOfUnitsSaar"] = "thousands_of_units_saar";
    FredScale["Units"] = "units";
    FredScale["Count"] = "count";
    FredScale["Index"] = "index";
    FredScale["DollarsPerBarrel"] = "dollars_per_barrel";
    FredScale["DollarsPerMetricTon"] = "dollars_per_metric_ton";
    FredScale["DollarsPerGallon"] = "dollars_per_gallon";
})(FredScale || (FredScale = {}));
const fredScaleDef = [
    FredScale.Percent,
    FredScale.NetPercentOfBanks,
    FredScale.PercentOfBalances,
    FredScale.PercentOfFirms,
    FredScale.ThousandsOfPersons,
    FredScale.MillionsOfDollarsSaar,
    FredScale.MillionsOfDollars,
    FredScale.MillionsOfChained2017Dollars,
    FredScale.ThousandsOfUnitsSaar,
    FredScale.Units,
    FredScale.Count,
    FredScale.Index,
    FredScale.DollarsPerBarrel,
    FredScale.DollarsPerMetricTon,
    FredScale.DollarsPerGallon,
];
const fredScaleSchema = z.enum(fredScaleDef);
(function (FredScale) {
    const displayNames = {
        [FredScale.Percent]: "%",
        [FredScale.NetPercentOfBanks]: "Net % of banks",
        [FredScale.PercentOfBalances]: "% of balances",
        [FredScale.PercentOfFirms]: "% of firms",
        [FredScale.ThousandsOfPersons]: "Thousands of persons",
        [FredScale.MillionsOfDollarsSaar]: "$ millions, SAAR",
        [FredScale.MillionsOfDollars]: "$ millions",
        [FredScale.MillionsOfChained2017Dollars]: "$ millions, chained 2017",
        [FredScale.ThousandsOfUnitsSaar]: "Thousands of units, SAAR",
        [FredScale.Units]: "Units",
        [FredScale.Count]: "Count",
        [FredScale.Index]: "Index",
        [FredScale.DollarsPerBarrel]: "$ / barrel",
        [FredScale.DollarsPerMetricTon]: "$ / metric ton",
        [FredScale.DollarsPerGallon]: "$ / gallon",
    };
    function displayName(value) {
        return displayNames[value];
    }
    FredScale.displayName = displayName;
    function values() {
        return fredScaleDef;
    }
    FredScale.values = values;
    function options() {
        return fredScaleDef.map((v) => ({ value: v, label: displayNames[v] }));
    }
    FredScale.options = options;
})(FredScale || (FredScale = {}));
var FredMetricFamily;
(function (FredMetricFamily) {
    FredMetricFamily["ConstructionEmployment"] = "construction_employment";
    FredMetricFamily["ConstructionGdp"] = "construction_gdp";
    FredMetricFamily["Permits"] = "permits";
    FredMetricFamily["Unemployment"] = "unemployment";
    FredMetricFamily["RealGdp"] = "real_gdp";
})(FredMetricFamily || (FredMetricFamily = {}));
const fredMetricFamilyDef = [
    FredMetricFamily.ConstructionEmployment,
    FredMetricFamily.ConstructionGdp,
    FredMetricFamily.Permits,
    FredMetricFamily.Unemployment,
    FredMetricFamily.RealGdp,
];
const fredMetricFamilySchema = z.enum(fredMetricFamilyDef);
(function (FredMetricFamily) {
    const displayNames = {
        [FredMetricFamily.ConstructionEmployment]: "Construction employment",
        [FredMetricFamily.ConstructionGdp]: "Construction GDP",
        [FredMetricFamily.Permits]: "Building permits",
        [FredMetricFamily.Unemployment]: "Unemployment rate",
        [FredMetricFamily.RealGdp]: "Real GDP",
    };
    function displayName(value) {
        return displayNames[value];
    }
    FredMetricFamily.displayName = displayName;
    function values() {
        return fredMetricFamilyDef;
    }
    FredMetricFamily.values = values;
    function options() {
        return fredMetricFamilyDef.map((v) => ({ value: v, label: displayNames[v] }));
    }
    FredMetricFamily.options = options;
})(FredMetricFamily || (FredMetricFamily = {}));
var FredSeriesSyncStatus;
(function (FredSeriesSyncStatus) {
    FredSeriesSyncStatus["Pending"] = "pending";
    FredSeriesSyncStatus["Succeeded"] = "succeeded";
    FredSeriesSyncStatus["Unavailable"] = "unavailable";
    FredSeriesSyncStatus["Failed"] = "failed";
})(FredSeriesSyncStatus || (FredSeriesSyncStatus = {}));
const fredSeriesSyncStatusDef = [
    FredSeriesSyncStatus.Pending,
    FredSeriesSyncStatus.Succeeded,
    FredSeriesSyncStatus.Unavailable,
    FredSeriesSyncStatus.Failed,
];
const fredSeriesSyncStatusSchema = z.enum(fredSeriesSyncStatusDef);
(function (FredSeriesSyncStatus) {
    const displayNames = {
        [FredSeriesSyncStatus.Pending]: "Pending",
        [FredSeriesSyncStatus.Succeeded]: "Succeeded",
        [FredSeriesSyncStatus.Unavailable]: "Unavailable",
        [FredSeriesSyncStatus.Failed]: "Failed",
    };
    function displayName(value) {
        return displayNames[value];
    }
    FredSeriesSyncStatus.displayName = displayName;
    function values() {
        return fredSeriesSyncStatusDef;
    }
    FredSeriesSyncStatus.values = values;
    function options() {
        return fredSeriesSyncStatusDef.map((v) => ({ value: v, label: displayNames[v] }));
    }
    FredSeriesSyncStatus.options = options;
})(FredSeriesSyncStatus || (FredSeriesSyncStatus = {}));
var FredSyncRunStatus;
(function (FredSyncRunStatus) {
    FredSyncRunStatus["Running"] = "running";
    FredSyncRunStatus["Succeeded"] = "succeeded";
    FredSyncRunStatus["Partial"] = "partial";
    FredSyncRunStatus["Failed"] = "failed";
})(FredSyncRunStatus || (FredSyncRunStatus = {}));
const fredSyncRunStatusDef = [
    FredSyncRunStatus.Running,
    FredSyncRunStatus.Succeeded,
    FredSyncRunStatus.Partial,
    FredSyncRunStatus.Failed,
];
const fredSyncRunStatusSchema = z.enum(fredSyncRunStatusDef);
(function (FredSyncRunStatus) {
    const displayNames = {
        [FredSyncRunStatus.Running]: "Running",
        [FredSyncRunStatus.Succeeded]: "Succeeded",
        [FredSyncRunStatus.Partial]: "Partially succeeded",
        [FredSyncRunStatus.Failed]: "Failed",
    };
    function displayName(value) {
        return displayNames[value];
    }
    FredSyncRunStatus.displayName = displayName;
    function values() {
        return fredSyncRunStatusDef;
    }
    FredSyncRunStatus.values = values;
    function options() {
        return fredSyncRunStatusDef.map((v) => ({ value: v, label: displayNames[v] }));
    }
    FredSyncRunStatus.options = options;
})(FredSyncRunStatus || (FredSyncRunStatus = {}));
var FredSyncScope;
(function (FredSyncScope) {
    FredSyncScope["National"] = "national";
    FredSyncScope["State"] = "state";
    FredSyncScope["All"] = "all";
})(FredSyncScope || (FredSyncScope = {}));
const fredSyncScopeDef = [
    FredSyncScope.National,
    FredSyncScope.State,
    FredSyncScope.All,
];
const fredSyncScopeSchema = z.enum(fredSyncScopeDef);
(function (FredSyncScope) {
    const displayNames = {
        [FredSyncScope.National]: "National",
        [FredSyncScope.State]: "Per-state",
        [FredSyncScope.All]: "All series",
    };
    function displayName(value) {
        return displayNames[value];
    }
    FredSyncScope.displayName = displayName;
    function values() {
        return fredSyncScopeDef;
    }
    FredSyncScope.values = values;
    function options() {
        return fredSyncScopeDef.map((v) => ({ value: v, label: displayNames[v] }));
    }
    FredSyncScope.options = options;
})(FredSyncScope || (FredSyncScope = {}));
var FredSyncTrigger;
(function (FredSyncTrigger) {
    FredSyncTrigger["Scheduled"] = "scheduled";
    FredSyncTrigger["Manual"] = "manual";
})(FredSyncTrigger || (FredSyncTrigger = {}));
const fredSyncTriggerDef = [FredSyncTrigger.Scheduled, FredSyncTrigger.Manual];
const fredSyncTriggerSchema = z.enum(fredSyncTriggerDef);
(function (FredSyncTrigger) {
    const displayNames = {
        [FredSyncTrigger.Scheduled]: "Scheduled",
        [FredSyncTrigger.Manual]: "Manual",
    };
    function displayName(value) {
        return displayNames[value];
    }
    FredSyncTrigger.displayName = displayName;
    function values() {
        return fredSyncTriggerDef;
    }
    FredSyncTrigger.values = values;
    function options() {
        return fredSyncTriggerDef.map((v) => ({ value: v, label: displayNames[v] }));
    }
    FredSyncTrigger.options = options;
})(FredSyncTrigger || (FredSyncTrigger = {}));
var PelContractorType;
(function (PelContractorType) {
    PelContractorType["GeneralContractorBuilding"] = "general_contractor_building";
    PelContractorType["GeneralContractorOther"] = "general_contractor_other";
    PelContractorType["HeavyHighwayRoad"] = "heavy_highway_road";
    PelContractorType["HeavyHighwayOther"] = "heavy_highway_other";
    PelContractorType["SubcontractorBuilding"] = "subcontractor_building";
    PelContractorType["DeveloperSubdivision"] = "developer_subdivision";
    PelContractorType["OtherSpecialty"] = "other_specialty";
})(PelContractorType || (PelContractorType = {}));
const pelContractorTypeDef = [
    PelContractorType.GeneralContractorBuilding,
    PelContractorType.GeneralContractorOther,
    PelContractorType.HeavyHighwayRoad,
    PelContractorType.HeavyHighwayOther,
    PelContractorType.SubcontractorBuilding,
    PelContractorType.DeveloperSubdivision,
    PelContractorType.OtherSpecialty,
];
const pelContractorTypeSchema = z.enum(pelContractorTypeDef);
(function (PelContractorType) {
    const displayNames = {
        [PelContractorType.GeneralContractorBuilding]: "General Contractor – Building",
        [PelContractorType.GeneralContractorOther]: "General Contractor – Other",
        [PelContractorType.HeavyHighwayRoad]: "Heavy/Highway – Road",
        [PelContractorType.HeavyHighwayOther]: "Heavy/Highway – Other",
        [PelContractorType.SubcontractorBuilding]: "Subcontractor – Building",
        [PelContractorType.DeveloperSubdivision]: "Developers/Subdivision",
        [PelContractorType.OtherSpecialty]: "Other/Specialty",
    };
    function displayName(value) {
        return displayNames[value];
    }
    PelContractorType.displayName = displayName;
    function values() {
        return pelContractorTypeDef;
    }
    PelContractorType.values = values;
    function options() {
        return pelContractorTypeDef.map((v) => ({ value: v, label: displayNames[v] }));
    }
    PelContractorType.options = options;
})(PelContractorType || (PelContractorType = {}));
var PelRegion;
(function (PelRegion) {
    PelRegion["NewYorkMetro"] = "new_york_metro";
    PelRegion["NonContinental"] = "non_continental";
    PelRegion["Northeast"] = "northeast";
    PelRegion["Southeast"] = "southeast";
    PelRegion["MidNorth"] = "mid_north";
    PelRegion["MidSouth"] = "mid_south";
    PelRegion["West"] = "west";
    PelRegion["MultiRegion"] = "multi_region";
})(PelRegion || (PelRegion = {}));
const pelRegionDef = [
    PelRegion.NewYorkMetro,
    PelRegion.NonContinental,
    PelRegion.Northeast,
    PelRegion.Southeast,
    PelRegion.MidNorth,
    PelRegion.MidSouth,
    PelRegion.West,
    PelRegion.MultiRegion,
];
const pelRegionSchema = z.enum(pelRegionDef);
(function (PelRegion) {
    const displayNames = {
        [PelRegion.NewYorkMetro]: "New York City/Metropolitan",
        [PelRegion.NonContinental]: "Non-Continental (PR, AK, HI)",
        [PelRegion.Northeast]: "Northeast (CT, DC, DE, MA, MD, ME, NH, NJ, NY, PA, RI, VT)",
        [PelRegion.Southeast]: "Southeast (AL, FL, GA, KY, MS, NC, SC, TN, VA, WV)",
        [PelRegion.MidNorth]: "Mid-north (IA, IL, IN, MI, MN, MT, ND, OH, SD, WI)",
        [PelRegion.MidSouth]: "Mid-south (AR, KS, LA, MO, NM, OK, TX)",
        [PelRegion.West]: "West (AZ, CA, CO, ID, NE, NV, OR, UT, WA, WY)",
        [PelRegion.MultiRegion]: "Multi-Region",
    };
    function displayName(value) {
        return displayNames[value];
    }
    PelRegion.displayName = displayName;
    function values() {
        return pelRegionDef;
    }
    PelRegion.values = values;
    function options() {
        return pelRegionDef.map((v) => ({ value: v, label: displayNames[v] }));
    }
    PelRegion.options = options;
})(PelRegion || (PelRegion = {}));
const naicsCodePattern = /^\d{6}-\d+$/;
const naicsCodeSchema = z
    .string()
    .regex(naicsCodePattern, { error: requiredOr("Invalid NAICS code") })
    .brand();
var NAICSCode;
(function (NAICSCode) {
    NAICSCode.of = (code) => naicsCodeSchema.parse(code);
    NAICSCode.format = (code, label) => `"${label}" [${code}]`;
})(NAICSCode || (NAICSCode = {}));
const baseNaicsCodePattern = /^\d{5,6}$/;
const baseNaicsCodeSchema = z
    .string()
    .regex(baseNaicsCodePattern, "Invalid base NAICS code");
const individualOwnerTypeSchema = z.enum(["owner", "spouse"]);
const IndividualOwnerType = individualOwnerTypeSchema.enum;
const companyCreditReportFailureReasonDef = ["vendor_error", "not_found"];
const companyCreditReportFailureReasonSchema = z.enum(companyCreditReportFailureReasonDef);
const CompanyCreditReportFailureReason = companyCreditReportFailureReasonSchema.enum;
const companyCreditReportVendorDef = ["cic", "experian"];
const companyCreditReportVendorSchema = z.enum(companyCreditReportVendorDef);
const CompanyCreditReportVendor = companyCreditReportVendorSchema.enum;
const individualCreditReportFailureReasonDef = [
    "vendor_error",
    "not_found",
    "no_score",
    "frozen",
    "insufficient_data",
];
const individualCreditReportFailureReasonSchema = z.enum(individualCreditReportFailureReasonDef);
const IndividualCreditReportFailureReason = individualCreditReportFailureReasonSchema.enum;
const individualCreditReportVendorDef = ["meridian", "experian"];
const individualCreditReportVendorSchema = z.enum(individualCreditReportVendorDef);
const IndividualCreditReportVendor = individualCreditReportVendorSchema.enum;
const individualOfacCheckFailureReasonDef = ["vendor_error", "insufficient_data"];
const individualOfacCheckFailureReasonSchema = z.enum(individualOfacCheckFailureReasonDef);
const IndividualOfacCheckFailureReason = individualOfacCheckFailureReasonSchema.enum;
const monthShortEnumDef = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];
const monthShortSchema = z.enum(monthShortEnumDef);
const monthShort = monthShortSchema.enum;
const userTypeDef = ["admin", "agent"];
const userTypeSchema = z.enum(userTypeDef);
const UserType = userTypeSchema.enum;
const companyBackgroundAttachmentTypeLabelMap = {
    contractors_questionnaire_application: "Contractors Questionnaire / Application",
    company_ownership_chart: "Company Ownership Chart",
    company_credit_report: "Company Credit Report",
    other_company_information: "Other Company Information",
};
const corporateFinancialsAttachmentTypeLabelMap = {
    fye_company_balance_sheet: "FYE Balance Sheet",
    fye_company_income_statement: "FYE Income Statement",
    fye_company_cash_flow_statement: "FYE Cash Flow Statement",
    fye_company_ar_aging: "FYE AR Aging",
    fye_company_ap_aging: "FYE AP Aging",
    interim_company_balance_sheet: "Interim Balance Sheet",
    interim_company_income_statement: "Interim Income Statement",
    interim_company_cash_flow_statement: "Interim Cash Flow Statement",
    interim_company_ar_aging: "Interim AR Aging",
    interim_company_ap_aging: "Interim AP Aging",
    company_tax_form: "Tax Form (Company)",
    other_company_financial_statement: "Other Corporate Financial Statement",
};
const wipAttachmentTypeLabelMap = {
    wip: "WIP Schedule",
    other_wip: "Other WIP Schedule",
};
const bankInformationAttachmentTypeLabelMap = {
    compliance_certificate: "Compliance Certificate",
    corporate_cash_verification: "Corporate Cash Verification",
    bank_line_summary: "Bank Line Summary",
    banking_reference: "Banking Reference",
    credit_agreement: "Credit Agreement",
    other_bank_statement: "Other Bank Statement",
};
const personalFinancialsAttachmentTypeLabelMap = {
    individual_financial_statement: "Individual Financial Statement",
    individual_cash_verification: "Individual Cash Verification",
    individual_credit_report: "Individual Credit Report",
    individual_tax_form: "Tax Form (Personal)",
    trust_document: "Trust Document",
    real_estate_schedule: "Real Estate Schedule",
    other_individual_financial_statement: "Other Individual Financial Statement",
};
const IndemnityAttachmentTypeLabelMap = {
    indemnity_agreement: "Completed Indemnity Agreement",
    indemnity_agreement_rider: "Indemnity Agreement Rider",
    collateral_document: "Collateral Document",
    subordination_agreement: "Subordination Agreement",
    other_indemnity_document: "Other Indemnity Document",
};
const bondAttachmentTypeLabelMap = {
    blank_bond_form: "Blank Bond Form",
    bond_request_form: "Bond Request Form",
    bid_package: "RFP / Bid Package",
    bid_results: "Bid Results",
    contract: "Contract",
    filled_bond_form: "Bond Form",
    job_cost_breakdown: "Job Cost Breakdown",
    poa: "Power of Attorney",
    shipping_label: "Shipping Label",
    invoice: "Invoice",
    rider: "Rider",
    other_bond_document: "Other Bond Document",
};
const financialStatementAttachmentTypeLabelMap = {
    fye_company_balance_sheet: "FYE Balance Sheet",
    fye_company_income_statement: "FYE Income Statement",
};
const defaultAttachmentTypeLabelMap = {
    ...companyBackgroundAttachmentTypeLabelMap,
    ...corporateFinancialsAttachmentTypeLabelMap,
    ...wipAttachmentTypeLabelMap,
    ...bankInformationAttachmentTypeLabelMap,
    ...personalFinancialsAttachmentTypeLabelMap,
    ...IndemnityAttachmentTypeLabelMap,
    ...bondAttachmentTypeLabelMap,
};
const attachmentGroupMap = new Map([
    ["Company", companyBackgroundAttachmentTypeLabelMap],
    ["Company Financials", corporateFinancialsAttachmentTypeLabelMap],
    ["Personal Financials", personalFinancialsAttachmentTypeLabelMap],
    ["Work in Progress", wipAttachmentTypeLabelMap],
    ["Banking", bankInformationAttachmentTypeLabelMap],
    ["Indemnity", IndemnityAttachmentTypeLabelMap],
    ["Bond", bondAttachmentTypeLabelMap],
]);
const contractSuretyTypeToLabelMap = {
    bid: "Bid",
    bid_to_final: "Final",
    final: "Final",
};
const bondStatusFilterOptionsDef = ["all", "open", "complete"];
const bondStatusFilterOptionsSchema = z.enum(bondStatusFilterOptionsDef);
const BondStatusFilterOptions = bondStatusFilterOptionsSchema.enum;
const bondTypeFilterOptionsDef = ["all", "bid", "final"];
const bondTypeFilterOptionsSchema = z.enum(bondTypeFilterOptionsDef);
const BondTypeFilterOptions = bondTypeFilterOptionsSchema.enum;
const entityRelationDef = ["quote", "bond", "account"];
const entityRelationSchema = z.enum(entityRelationDef);
const EntityRelations = entityRelationSchema.enum;
const adminNoteRelationDef = entityRelationDef;
const adminNoteRelationSchema = entityRelationSchema;
const AdminNoteRelation = adminNoteRelationSchema.enum;
const actionItemStatusDef = ["open", "complete"];
const actionItemStatusSchema = z.enum(actionItemStatusDef);
const actionItemStatus = actionItemStatusSchema.enum;
const errorKeyDef = [
    "max_file_size",
    "broker_of_record_account",
    "broker_of_record_bond",
    "broker_of_record_quote",
    "indemnity_individual_exists",
    "indemnity_company_exists",
    "indemnity_exists",
    "user_exists",
    "attachment_deleted",
    "model_downsized",
    "underwriter_lock",
    "bond_request_already_declined",
    "bond_request_already_in_review",
    "bond_request_already_closed",
    "bond_request_already_written",
    "bond_issuance_already_exists",
    "bond_request_already_open",
    "final_bond_already_requested",
    "bond_number_exists",
    "account_already_closed",
    "account_already_open",
    "tag_display_name_conflict",
    "tag_not_found",
    "entity_not_found",
    "account.conflict.stale_version",
    "bond.conflict.stale_version",
    "quote.conflict.stale_version",
    "bond_numbers_exhausted",
    "bonding_line_placement",
    "file_is_encrypted",
    "active_agent_follower_required",
    "wip_line_item_project_conflict",
    "wip_extraction_review_blocked",
    "validation_failed",
    "bonding_line_omitted",
    "bonding_lines_stale",
    "bonding_lines_not_publishable",
    "bonding_lines_no_placeable_line",
    "bonding_line_exists",
    "bondability_letter_unavailable",
    "default",
];
const errorKeySchema = z.enum(errorKeyDef);
const ErrorKey = errorKeySchema.enum;
const financialsPreparationMethodOptions = [
    { value: "internal", label: "Internal" },
    { value: "cpa_comp", label: "CPA Compilation" },
    { value: "cpa_review", label: "CPA Review" },
    { value: "cpa_audit", label: "CPA Audit" },
    { value: "does_not_keep", label: "Does not keep financial statements" },
];
const sessionTypeDef = [
    "contract",
    "contract_v2",
    "financial",
    "classification",
    "bond_request",
    "wip",
    "application",
];
const sessionTypeSchema = z.enum(sessionTypeDef);
const SessionType = sessionTypeSchema.enum;
const documentStatusDef = ["processing", "completed", "failed", "partial"];
const documentStatusSchema = z.enum(documentStatusDef);
const DocumentStatus = documentStatusSchema.enum;
const runnerStatusDef = [
    "pending",
    "running",
    "completed",
    "failed",
    "skipped",
    "partial",
];
const runnerStatusSchema = z.enum(runnerStatusDef);
const RunnerStatus = runnerStatusSchema.enum;
const ocrProviderDef = ["reducto"];
const ocrProviderSchema = z.enum(ocrProviderDef);
const OcrProvider = ocrProviderSchema.enum;
const llmProviderDef = ["anthropic", "openai", "google"];
const llmProviderSchema = z.enum(llmProviderDef);
const LlmProvider = llmProviderSchema.enum;
const CloudStorageBucketName = {
    DEFAULT: "default",
    RETENTION: "retention",
    TMP: "tmp",
    CARRIERS: "carriers",
    FORMS: "forms",
    COMPLETED_DOCUMENTS: "completed-documents",
    PROMPT_RUNNER: "prompt-runner",
    SLACK: "slack",
};
const cloudStorageBucketNameValues = Object.values(CloudStorageBucketName);
const cloudStorageBucketNameSchema = z.enum(cloudStorageBucketNameValues);
const assistantContextTypeDef = ["account", "bond_request", "bond", "global"];
const assistantContextTypeSchema = z.enum(assistantContextTypeDef);
const AssistantContextType = assistantContextTypeSchema.enum;
const assistantConversationStatusDef = ["open", "archived"];
const assistantConversationStatusSchema = z.enum(assistantConversationStatusDef);
const AssistantConversationStatus = assistantConversationStatusSchema.enum;
const assistantMessageRoleDef = ["user", "assistant"];
const assistantMessageRoleSchema = z.enum(assistantMessageRoleDef);
const AssistantMessageRole = assistantMessageRoleSchema.enum;
const assistantFeedbackRatingDef = ["up", "down"];
const assistantFeedbackRatingSchema = z.enum(assistantFeedbackRatingDef);
const AssistantFeedbackRating = assistantFeedbackRatingSchema.enum;

export { AccountingStandard, AdminNoteRelation, AdminUserRole, AgentUserRole, AssistantContextType, AssistantConversationStatus, AssistantFeedbackRating, AssistantMessageRole, AttachmentFileType, AttachmentSource, Auth0UserRole, BankInformationAttachmentFileType, BidBondAmountType, BidContractSuretyBondFormType, BidSuretyBondClosedReason, BondFormFileType, BondStatusFilterOptions, BondTypeFilterOptions, CLOSED_REASON_NOTE_MAX_LENGTH, ClosedReason, CloudStorageBucketName, CommercialSuretyBondType, CompanyBackgroundAttachmentFileType, CompanyCreditReportFailureReason, CompanyCreditReportVendor, CompanyFinancialStatementPreparationMethod, ComponentExtractionStatus, ContactType, ContractBondSubtype, ContractCategoryBondType, ContractDamage, ContractDamageDescriptions, ContractHazard, ContractHazardDescriptions, ContractSuretyAccountUnderwritingModelSize, ContractSuretyBondFormType, ContractSuretyBondType, ContractSuretyType, CorporateFinancialsAttachmentFileType, DataSource, DocumentStatus, ESignatureDocumentType, ESignatureMethod, ESignatureStatus, ESignatureVendor, EmailFileExtension, EmailForwardParseMethod, EmailSubmissionAttachmentCascadeStatus, EmailSubmissionConversionStatus, EmailSubmissionStatus, EntityRelations, ErrorKey, ExcelFileExtension, ExposureSize, ExtractionErrorType, ExtractionStatus, FeatureFlagName, FileClassificationStatus, FileExtension, FileType, FinalContractSuretyBondClosedReason, FinalContractSuretyBondFormType, FinancialPeriodPreparationMethod, FinancialStatementAttachmentFileType, FinancialStatementComponent, FredFrequency, FredMetricFamily, FredScale, FredSeasonalAdjustment, FredSeriesGroup, FredSeriesSyncStatus, FredSyncRunStatus, FredSyncScope, FredSyncTrigger, GeneratedFileType, IndemnificationType, IndemnitorType, IndemnityAgreementSignatureMethod, IndemnityAttachmentFileType, IndemnityAttachmentTypeLabelMap, IndemnityStatus, IndividualCreditReportFailureReason, IndividualCreditReportVendor, IndividualOfacCheckFailureReason, IndividualOwnerType, LlmProvider, NAICSCode, NaicsCodeKind, NameSuffix, OcrProvider, PelContractorType, PelRegion, PeriodType, PersonalFinancialsAttachmentFileType, PowerPointFileExtension, RevenueRecognition, RunnerStatus, SessionType, SfaaClassExperienceImportStatus, StatementType, SuretyAccountAttachmentFileType, SuretyAccountStatus, SuretyAccountUnderwritingStatus, SuretyBondFormTemplateClaimResponse, SuretyBondFormTemplateDefaultTrigger, SuretyBondFormToReviewStatus, SuretyBondFormType, SuretyBondIssuanceMethod, SuretyBondRenewalMethod, SuretyBondStatus, SuretyBondTermAdjustmentType, SuretyBondTermPaymentStatus, SuretyBondType, SuretyQuoteStatus, SuretyQuoteType, SuretyQuoteUnderwritingStatus, SuretyType, USStateOrTerritory, UserType, WipAttachmentFileType, WipExtractionReviewStatus, WordFileExtension, accountingStandardDef, accountingStandardSchema, actionItemStatus, actionItemStatusDef, actionItemStatusSchema, adminNoteRelationDef, adminNoteRelationSchema, adminUserRoleDef, adminUserRoleSchema, adobeFileExtensionDef, agentUserRoleDef, agentUserRoleSchema, assistantContextTypeDef, assistantContextTypeSchema, assistantConversationStatusDef, assistantConversationStatusSchema, assistantFeedbackRatingDef, assistantFeedbackRatingSchema, assistantMessageRoleDef, assistantMessageRoleSchema, attachmentFileTypeDef, attachmentFileTypeSchema, attachmentGroupMap, attachmentSourceDef, attachmentSourceSchema, auth0UserRoleDef, auth0UserRoleSchema, bankInformationAttachmentFileTypeSchema, bankInformationAttachmentTypeLabelMap, bankInformationFileTypeDef, baseNaicsCodeSchema, bidBondAmountTypeDef, bidBondAmountTypeSchema, bidContractSuretyBondFormTypeDef, bidContractSuretyBondFormTypeSchema, bidSuretyBondClosedReasonDef, bidSuretyBondClosedReasonSchema, bondAttachmentTypeLabelMap, bondFormFileTypeDef, bondFormFileTypeSchema, bondRequestBondTypeDef, bondRequestBondTypeSchema, bondStatusFilterOptionsDef, bondStatusFilterOptionsSchema, bondTypeFilterOptionsDef, bondTypeFilterOptionsSchema, classificationPredictionSchema, classifierSchema, classifyDeferReasonSchema, closedReasonDef, closedReasonSchema, cloudStorageBucketNameSchema, commercialSuretyBondTypeDef, commercialSuretyBondTypeSchema, companyBackgroundAttachmentFileTypeSchema, companyBackgroundAttachmentTypeLabelMap, companyBackgroundFileTypeDef, companyCreditReportFailureReasonDef, companyCreditReportFailureReasonSchema, companyCreditReportVendorDef, companyCreditReportVendorSchema, companyFinancialStatementPreparationMethodDef, companyFinancialStatementPreparationMethodSchema, componentExtractionStatusDef, componentExtractionStatusSchema, contactTypeDef, contactTypeSchema, contractBondSubtypeDef, contractBondSubtypeSchema, contractCategoryBondTypeDef, contractCategoryBondTypeSchema, contractDamageDef, contractDamageSchema, contractHazardDef, contractHazardSchema, contractSuretyAccountUnderwritingModelSizeDef, contractSuretyAccountUnderwritingModelSizeSchema, contractSuretyBondFormTypeDef, contractSuretyBondFormTypeSchema, contractSuretyBondTypeDef, contractSuretyBondTypeSchema, contractSuretyTypeDef, contractSuretyTypeSchema, contractSuretyTypeToLabelMap, corporateFinancialsAttachmentFileTypeSchema, corporateFinancialsAttachmentTypeLabelMap, corporateFinancialsFileTypeDef, dataSourceDef, dataSourceSchema, dateTimeSchema, decisionAnswerSchema, defaultAttachmentTypeLabelMap, documentStatusDef, documentStatusSchema, eSignatureDocumentTypeDef, eSignatureDocumentTypeSchema, eSignatureMethodDef, eSignatureMethodSchema, eSignatureStatusDef, eSignatureStatusSchema, eSignatureVendorDef, eSignatureVendorSchema, emailFileExtensionDef, emailFileExtensionSchema, emailForwardParseMethodDef, emailForwardParseMethodSchema, emailSubmissionAttachmentCascadeStatusDef, emailSubmissionAttachmentCascadeStatusSchema, emailSubmissionConversionStatusDef, emailSubmissionConversionStatusSchema, emailSubmissionStatusDef, emailSubmissionStatusSchema, entityRelationDef, entityRelationSchema, errorKeyDef, errorKeySchema, excelBinaryFormatsDef, excelFileExtensionDef, excelFileExtensionSchema, excelXMLFormatsDef, expectedExtractionErrorTypeDef, exposureSizeSchema, extractionErrorTypeDef, extractionErrorTypeSchema, extractionStatusDef, extractionStatusSchema, featureFlagNameSchema, fileClassificationStatusDef, fileClassificationStatusSchema, fileExtensionDef, fileExtensionSchema, fileTypeDef, fileTypeSchema, finalContractSuretyBondClosedReasonDef, finalContractSuretyBondClosedReasonSchema, finalContractSuretyBondFormTypeDef, finalContractSuretyBondFormTypeSchema, financialPeriodPreparationMethodDef, financialPeriodPreparationMethodSchema, financialStatementAttachmentFileTypeSchema, financialStatementAttachmentTypeLabelMap, financialStatementComponentDef, financialStatementComponentSchema, financialStatementFileTypeDef, financialsPreparationMethodOptions, fredFrequencyDef, fredFrequencySchema, fredMetricFamilyDef, fredMetricFamilySchema, fredScaleDef, fredScaleSchema, fredSeasonalAdjustmentDef, fredSeasonalAdjustmentSchema, fredSeriesGroupDef, fredSeriesGroupSchema, fredSeriesSyncStatusDef, fredSeriesSyncStatusSchema, fredSyncRunStatusDef, fredSyncRunStatusSchema, fredSyncScopeDef, fredSyncScopeSchema, fredSyncTriggerDef, fredSyncTriggerSchema, generatedFileTypeDef, generatedFileTypeSchema, indemnificationTypeDef, indemnificationTypeSchema, indemnitorTypeDef, indemnitorTypeSchema, indemnityAgreementSignatureMethodDef, indemnityAgreementSignatureMethodSchema, indemnityAttachmentFileTypeSchema, indemnityFileTypeDef, indemnityStatusDef, indemnityStatusSchema, individualCreditReportFailureReasonDef, individualCreditReportFailureReasonSchema, individualCreditReportVendorDef, individualCreditReportVendorSchema, individualOfacCheckFailureReasonDef, individualOfacCheckFailureReasonSchema, individualOwnerTypeSchema, isExpectedExtractionErrorType, isoFileExtensionDef, llmProviderDef, llmProviderSchema, monthShort, monthShortEnumDef, monthShortSchema, msBinaryFormatsDef, msFileExtensionDef, msXMLFormatsDef, naicsCodeKindDef, naicsCodeKindSchema, naicsCodeSchema, nameSuffixDef, nameSuffixSchema, ocrProviderDef, ocrProviderSchema, pelContractorTypeDef, pelContractorTypeSchema, pelRegionDef, pelRegionSchema, periodTypeDef, periodTypeSchema, personalFinancialsAttachmentFileTypeSchema, personalFinancialsAttachmentTypeLabelMap, personalFinancialsFileTypeDef, powerPointBinaryFormatsDef, powerPointFileExtensionDef, powerPointFileExtensionSchema, powerPointXMLFormatsDef, revenueRecognitionDef, revenueRecognitionSchema, runnerStatusDef, runnerStatusSchema, sessionTypeDef, sessionTypeSchema, sfaaClassExperienceImportStatusDef, sfaaClassExperienceImportStatusSchema, statementTypeDef, statementTypeSchema, suretyAccountAttachmentFileTypeDef, suretyAccountAttachmentFileTypeSchema, suretyAccountStatusDef, suretyAccountStatusSchema, suretyAccountUnderwritingStatusDef, suretyAccountUnderwritingStatusSchema, suretyBondFormTemplateClaimResponseDef, suretyBondFormTemplateClaimResponseSchema, suretyBondFormTemplateDefaultTriggerDef, suretyBondFormTemplateDefaultTriggerSchema, suretyBondFormToReviewStatusDef, suretyBondFormToReviewStatusSchema, suretyBondFormTypeDef, suretyBondFormTypeSchema, suretyBondIssuanceMethodDef, suretyBondIssuanceMethodSchema, suretyBondRenewalMethodDef, suretyBondRenewalMethodSchema, suretyBondStatusDef, suretyBondStatusSchema, suretyBondTermAdjustmentTypeDef, suretyBondTermAdjustmentTypeSchema, suretyBondTermPaymentStatusDef, suretyBondTermPaymentStatusSchema, suretyBondTypeDef, suretyBondTypeSchema, suretyQuoteStatusDef, suretyQuoteStatusSchema, suretyQuoteTypeDef, suretyQuoteTypeSchema, suretyQuoteUnderwritingStatusDef, suretyQuoteUnderwritingStatusSchema, suretyTypeDef, suretyTypeDisplayName, suretyTypeOf, suretyTypeSchema, usStateOrTerritoryDef, usStateOrTerritorySchema, userTypeDef, userTypeSchema, wipAttachmentFileTypeSchema, wipAttachmentTypeLabelMap, wipExtractionReviewStatusDef, wipExtractionReviewStatusSchema, wipFileTypeDef, wordBinaryFormatsDef, wordFileExtensionDef, wordFileExtensionSchema, wordXMLFormatsDef };
